import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  billingService,
  type EntitlementSnapshot,
  type PurchaseStateSnapshot,
} from '@/services/billingService';
import { flagService } from '@/services/flagService';
import { registerAppLifecycleHandlers } from '@/utils/appLifecycle';

interface UseBillingResult {
  purchaseState: PurchaseStateSnapshot;
  entitlement: EntitlementSnapshot | null;
  loading: boolean;
  error: string | null;
  purchase: () => Promise<void>;
  restorePurchases: () => Promise<void>;
  validateReceipt: typeof billingService.validateReceipt;
  getEntitlement: (forceRefresh?: boolean) => Promise<EntitlementSnapshot>;
  refresh: () => Promise<void>;
}

export function useBilling(): UseBillingResult {
  const [purchaseState, setPurchaseState] = useState<PurchaseStateSnapshot>(billingService.getPurchaseState());
  const [entitlement, setEntitlement] = useState<EntitlementSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const nextState = billingService.getPurchaseState();
    setPurchaseState(nextState);
    const nextEntitlement = await billingService.getEntitlement(true);
    setEntitlement(nextEntitlement);
    setError(nextState.state === 'failed' ? nextState.failureReason : null);
    setLoading(false);
  }, []);

  const purchase = useCallback(async () => {
    setLoading(true);
    const next = await billingService.purchase();
    setPurchaseState(next);
    const nextEntitlement = await billingService.getEntitlement(true);
    setEntitlement(nextEntitlement);
    setError(next.state === 'failed' ? next.failureReason : null);
    setLoading(false);
  }, []);

  const restorePurchases = useCallback(async () => {
    setLoading(true);
    const next = await billingService.restorePurchases();
    setPurchaseState(next);
    const nextEntitlement = await billingService.getEntitlement(true);
    setEntitlement(nextEntitlement);
    setError(next.state === 'failed' ? next.failureReason : null);
    setLoading(false);
  }, []);

  const getEntitlement = useCallback(async (forceRefresh = false) => {
    const snapshot = await billingService.getEntitlement(forceRefresh);
    setEntitlement(snapshot);
    return snapshot;
  }, []);

  const safeRefreshFlags = useCallback(async () => {
    try {
      await flagService.refresh();
    } catch {
      // Fail-closed: billing lifecycle must continue even if config fetch fails.
    }
  }, []);

  useEffect(() => {
    const teardown = registerAppLifecycleHandlers({
      onColdStart: async () => {
        await safeRefreshFlags();
        const recovered = await billingService.recoverPendingState();
        setPurchaseState(recovered);
        const nextEntitlement = await billingService.getEntitlement(true);
        setEntitlement(nextEntitlement);
        setError(recovered.state === 'failed' ? recovered.failureReason : null);
        setLoading(false);
      },
      onResume: async () => {
        await safeRefreshFlags();
        const current = billingService.getPurchaseState();
        let entitlementSnapshot = await billingService.getEntitlement(false);
        const needsForceEntitlement =
          current.state === 'pending' || entitlementSnapshot.state === 'failed';
        if (needsForceEntitlement) {
          entitlementSnapshot = await billingService.getEntitlement(true);
        }
        if (current.state === 'pending' || entitlementSnapshot.state === 'failed') {
          const recovered = await billingService.recoverPendingState();
          setPurchaseState(recovered);
          setError(recovered.state === 'failed' ? recovered.failureReason : null);
        } else {
          setPurchaseState(current);
          setError(current.state === 'failed' ? current.failureReason : null);
        }
        setEntitlement(entitlementSnapshot);
      },
    });

    return teardown;
  }, [safeRefreshFlags]);

  return useMemo(() => ({
    purchaseState,
    entitlement,
    loading,
    error,
    purchase,
    restorePurchases,
    validateReceipt: billingService.validateReceipt.bind(billingService),
    getEntitlement,
    refresh,
  }), [purchaseState, entitlement, loading, error, purchase, restorePurchases, getEntitlement, refresh]);
}
